// Buff catalog. Vanilla classic combat logs DO NOT record SPELL_AURA_APPLIED
// for buffs cast pre-pull (when no one was in combat yet) — paladin blessings,
// druid Mark of the Wild, priest Prayer of Fortitude and so on are usually
// applied 30s before the pull and never refresh during the fight, so WCL's
// per-fight buff query returns zero events for them. Same story for world
// buffs (Onyxia / Songflower / Zandalar), consumables (Mongoose / Giants /
// Juju Power / Juju Might / Winterfall Firewater) and food.
//
// To reflect reality, the user picks a faction + toggles which buffs they
// actually had on. The frontend tags those as origin='assumed', and the
// "战斗中（含 buff）" stats view uses the merged buff effects to recompute
// AP / Crit / Strength / etc. on top of the base (gear+enchants+race) values
// — so we don't depend on WCL's CombatantInfo snapshot which is unreliable
// for vanilla classic.

export type Faction = 'alliance' | 'horde' | 'auto';
export type FactionTag = 'alliance' | 'horde' | 'both';
export type BuffCategory = 'raid' | 'world' | 'consumable' | 'food' | 'self';

// Effect of a single buff on the character's stat budget. Empty fields default
// to 0. attrMult is the rare multiplicative attribute booster (Greater Blessing
// of Kings = 1.10, Spirit of Zandalar = 1.15) — both apply independently to
// the SAME (base+additive) attribute total, so they stack multiplicatively
// (e.g. Kings × Zandalar = 1.265× total stat multiplier).
export interface BuffEffects {
  str?: number;
  agi?: number;
  sta?: number;
  int?: number;
  spi?: number;
  ap?: number;          // flat melee/ranged AP
  crit?: number;        // % points (5 = +5%)
  hit?: number;         // % points
  armor?: number;       // flat
  attrMult?: number;    // 1.10 = ×1.10 on str/agi/sta/int/spi
}

export interface BuffDef {
  spellId: number;
  enKey: string;        // short English label, mostly for code readability
  category: BuffCategory;
  faction?: FactionTag; // only for raid buffs (paladin = alliance, shaman = horde)
  defaultOn: boolean;   // tick by default in the picker
  source?: 'paladin' | 'druid' | 'priest' | 'shaman' | 'warrior' | 'hunter' | 'world' | 'item' | 'self';
  iconName: string;     // pre-resolved wowhead icon name
  effects: BuffEffects;
}

// Backward-compatible alias for legacy callers.
export type RaidBuffDef = BuffDef;

// Order is the order they show up in the UI.
// Spell IDs target the rank applied to a level-60 raider in Naxx-era vanilla.
// Effect numbers are the "with-Imp-talent" values typical of a min-max raid
// (Imp Mark of the Wild 5/5, Imp Prayer of Fortitude 5/5, Imp Strength of
// Earth 5/5, Imp Grace of Air 5/5, Imp Battle Shout 5/5). If the user runs
// without those talents the buff value will be ~10-15% lower than displayed.
export const BUFF_CATALOG: readonly BuffDef[] = [
  // ============================================================
  // Raid buffs (paladin / druid / priest / shaman / hunter)
  // ============================================================
  // Paladin — alliance only. Greater blessings exist in vanilla 1.10+.
  { spellId: 25898, enKey: 'Greater Blessing of Kings',     category: 'raid', faction: 'alliance', defaultOn: true,  source: 'paladin', iconName: 'spell_magic_greaterblessingofkings',
    effects: { attrMult: 1.10 } },
  { spellId: 25916, enKey: 'Greater Blessing of Might',     category: 'raid', faction: 'alliance', defaultOn: true,  source: 'paladin', iconName: 'spell_holy_greaterblessingofkings',
    effects: { ap: 222 } },                          // R8 base; without imp BoM (no such talent in vanilla)
  { spellId: 25894, enKey: 'Greater Blessing of Wisdom',    category: 'raid', faction: 'alliance', defaultOn: false, source: 'paladin', iconName: 'spell_holy_greaterblessingofwisdom',
    effects: {} },                                   // mp5; no melee stat effect
  { spellId: 25899, enKey: 'Greater Blessing of Sanctuary', category: 'raid', faction: 'alliance', defaultOn: false, source: 'paladin', iconName: 'spell_holy_greaterblessingofsanctuary',
    effects: {} },                                   // damage shield + threat; no flat stat
  { spellId: 25895, enKey: 'Greater Blessing of Salvation', category: 'raid', faction: 'alliance', defaultOn: false, source: 'paladin', iconName: 'spell_holy_greaterblessingofsalvation',
    effects: {} },                                   // -30% threat; no stat

  // Druid — both factions. Base GotW R3 = +12 to all stats; Imp MotW 5/5
  // (35% bonus) → +16. Use BASE here since not every druid has the talent.
  { spellId: 21850, enKey: 'Gift of the Wild',              category: 'raid', faction: 'both',     defaultOn: true,  source: 'druid',   iconName: 'spell_nature_regeneration',
    effects: { str: 12, agi: 12, sta: 12, int: 12, spi: 12 } },

  // Priest — both factions. Base PoF R3 = +54 sta; Imp Fortitude 5/5 (20%
  // bonus) → +65. Use BASE here, same reason as MotW.
  { spellId: 21564, enKey: 'Prayer of Fortitude',           category: 'raid', faction: 'both',     defaultOn: true,  source: 'priest',  iconName: 'spell_holy_prayeroffortitude',
    effects: { sta: 54 } },
  { spellId: 27683, enKey: 'Prayer of Shadow Protection',   category: 'raid', faction: 'both',     defaultOn: false, source: 'priest',  iconName: 'spell_holy_prayerofshadowprotection',
    effects: {} },                                   // shadow resist; no melee stat

  // Hunter — both factions. Trueshot Aura R4 = +100 melee/ranged AP within 30y.
  { spellId: 20906, enKey: 'Trueshot Aura',                 category: 'raid', faction: 'both',     defaultOn: true,  source: 'hunter',  iconName: 'ability_trueshot',
    effects: { ap: 100 } },

  // Shaman — horde only. Imp 5/5 talents bump these by ~20%.
  { spellId: 25361, enKey: 'Strength of Earth Totem',       category: 'raid', faction: 'horde',    defaultOn: true,  source: 'shaman',  iconName: 'spell_nature_earthbindtotem',
    effects: { str: 86 } },
  { spellId: 25359, enKey: 'Grace of Air Totem',            category: 'raid', faction: 'horde',    defaultOn: true,  source: 'shaman',  iconName: 'spell_nature_invisibilitytotem',
    effects: { agi: 77 } },
  { spellId: 25587, enKey: 'Windfury Totem',                category: 'raid', faction: 'horde',    defaultOn: true,  source: 'shaman',  iconName: 'spell_nature_windfury',
    effects: {} },                                   // proc: extra attack with bonus AP; not a stat
  { spellId: 25570, enKey: 'Mana Spring Totem',             category: 'raid', faction: 'horde',    defaultOn: false, source: 'shaman',  iconName: 'spell_nature_manaregentotem',
    effects: {} },                                   // mp5; no melee stat

  // ============================================================
  // World buffs — applied via NPC turn-ins or world-spawn flowers.
  // None of these can be observed by WCL's per-fight buff query
  // (they're applied way before the pull) so the user toggles them.
  // ============================================================
  // Default ON for the staple naxx-era world buffs — assumption is the
  // user is a min-maxing fury warrior who picked up everything. Toggle
  // off in the picker if a particular pull was world-buff-less.
  { spellId: 22888, enKey: 'Rallying Cry of the Dragonslayer', category: 'world', defaultOn: true,  source: 'world', iconName: 'inv_misc_head_dragon_01',
    effects: { ap: 140, crit: 5 } },                 // Onyxia / Nefarian head turn-in
  { spellId: 15366, enKey: 'Songflower Serenade',           category: 'world', defaultOn: true,  source: 'world', iconName: 'spell_holy_mindvision',
    effects: { str: 5, agi: 5, sta: 5, int: 5, spi: 5, crit: 5 } },
  { spellId: 24425, enKey: 'Spirit of Zandalar',            category: 'world', defaultOn: true,  source: 'world', iconName: 'spell_holy_mindsooth',
    effects: { attrMult: 1.15 } },                   // ZG turn-ins; stacks ×1.15 with Kings ×1.10
  { spellId: 22817, enKey: 'Mol\'dar\'s Moxie',             category: 'world', defaultOn: true,  source: 'world', iconName: 'spell_holy_sealofvalor',
    effects: { sta: 30 } },                          // Dire Maul tribute
  { spellId: 22818, enKey: 'Fengus\' Ferocity',             category: 'world', defaultOn: true,  source: 'world', iconName: 'spell_holy_blessingofstamina',
    effects: { ap: 200 } },                          // Dire Maul tribute
  { spellId: 22820, enKey: 'Slip\'kik\'s Savvy',            category: 'world', defaultOn: false, source: 'world', iconName: 'spell_holy_holysmite',
    effects: {} },                                   // +3% spell crit; n/a melee
  { spellId: 23768, enKey: 'Sayge\'s Dark Fortune of Damage', category: 'world', defaultOn: false, source: 'world', iconName: 'inv_misc_orb_02',
    effects: {} },                                   // +10% all damage; not a stat — DPS multiplier handled separately

  // ============================================================
  // Consumables — flasks / elixirs / Juju / Winterfall.
  // Vanilla stack rules ARE NOT enforced here (battle-elixir vs guardian
  // vs misc); user is responsible for picking what they actually have.
  // ============================================================
  // Default ON for fury BiS consumable kit. Mongoose wins over Giants in
  // the battle-elixir slot for fury (crit > raw str), so Mongoose is on
  // and Giants is off — they don't stack. Juju Power / Juju Might /
  // Winterfall are all separate stack categories so they all stack.
  { spellId: 17538, enKey: 'Elixir of the Mongoose',        category: 'consumable', defaultOn: true,  source: 'item', iconName: 'inv_potion_32',
    effects: { agi: 25, crit: 2 } },                 // battle elixir
  { spellId: 17540, enKey: 'Elixir of Giants',              category: 'consumable', defaultOn: false, source: 'item', iconName: 'inv_potion_61',
    effects: { str: 25 } },                          // battle elixir; competes with Mongoose
  { spellId: 16322, enKey: 'Juju Power',                    category: 'consumable', defaultOn: true,  source: 'item', iconName: 'inv_misc_monsterscales_07',
    effects: { str: 30 } },                          // 60min, separate stack
  { spellId: 16329, enKey: 'Juju Might',                    category: 'consumable', defaultOn: true,  source: 'item', iconName: 'inv_misc_monsterscales_11',
    effects: { ap: 40 } },                           // 30min, separate stack
  { spellId: 17038, enKey: 'Winterfall Firewater',          category: 'consumable', defaultOn: true,  source: 'item', iconName: 'inv_potion_92',
    effects: { ap: 35 } },                           // 20min

  // ============================================================
  // Food — only one is active at a time (well-fed buff).
  // ============================================================
  // Only ONE food (well-fed) buff is active at a time. Smoked Desert
  // Dumplings is the fury staple (+20 str). Default it on; user can swap
  // to Squid (alliance dual-wield rogue/hunter food) or Sunfruit if
  // they prefer.
  { spellId: 18192, enKey: 'Smoked Desert Dumplings',       category: 'food', defaultOn: true,  source: 'item', iconName: 'inv_misc_food_15',
    effects: { str: 20 } },                          // +20 str / 15 min
  { spellId: 18194, enKey: 'Grilled Squid',                 category: 'food', defaultOn: false, source: 'item', iconName: 'inv_misc_fish_24',
    effects: { agi: 10 } },                          // +10 agi / 15 min
  { spellId: 23697, enKey: 'Blessed Sunfruit',              category: 'food', defaultOn: false, source: 'item', iconName: 'inv_misc_food_12',
    effects: { str: 10 } },
  { spellId: 18141, enKey: 'Tender Wolf Steak',             category: 'food', defaultOn: false, source: 'item', iconName: 'inv_misc_food_55',
    effects: { sta: 12, spi: 12 } },

  // ============================================================
  // Self buffs — what the warrior puts on themselves at pull start.
  // Battle Shout shows up here so user can toggle it for the buffed view
  // (WCL captures this at fight start, but vanilla CombatantInfo doesn't
  // always reflect it, so we let the user decide).
  // ============================================================
  { spellId: 25289, enKey: 'Battle Shout',                  category: 'self', defaultOn: true,  source: 'self', iconName: 'ability_warrior_battleshout',
    effects: { ap: 193 } },                          // R7 base = 193 AP; Imp BS 5/5 → 241; Imp BS 2/5 → 212. Use base.
];

// Legacy export — same shape as the old RAID_BUFFS, filtered to category 'raid'.
// Existing call sites (raidBuffIcon, buffsForFaction, etc.) keep working.
export const RAID_BUFFS: readonly BuffDef[] = BUFF_CATALOG.filter((b) => b.category === 'raid');

// Lookup: given a spellId, return the catalog icon if present.
export function raidBuffIcon(spellId: number): string | null {
  for (const b of BUFF_CATALOG) if (b.spellId === spellId) return b.iconName;
  return null;
}

// Filter to RAID buffs available for a given faction selection. World /
// consumable / food / self buffs ignore faction.
export function buffsForFaction(faction: Faction): BuffDef[] {
  return RAID_BUFFS.filter((b) => {
    if (b.faction === 'both') return true;
    if (faction === 'alliance') return b.faction === 'alliance';
    if (faction === 'horde')    return b.faction === 'horde';
    return false; // 'auto' should be resolved before calling this
  });
}

// Buffs of a given category. World / consumable / food / self are
// faction-agnostic so we just return the category filter.
export function buffsByCategory(category: BuffCategory): BuffDef[] {
  return BUFF_CATALOG.filter((b) => b.category === category);
}

// Heuristic: detect raid faction from the player class roster.
// Vanilla: shamans are horde-only, paladins are alliance-only.
export function detectFaction(classes: ReadonlyArray<string>): 'alliance' | 'horde' | 'unknown' {
  const lower = classes.map((c) => c.toLowerCase());
  const hasPaladin = lower.includes('paladin');
  const hasShaman = lower.includes('shaman');
  if (hasPaladin && !hasShaman) return 'alliance';
  if (hasShaman && !hasPaladin) return 'horde';
  return 'unknown';
}
